import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { freelancerService } from '../services/api';
import { Freelancer } from '../types';
import { Users, Star, DollarSign, Clock, Globe, Search, Filter, AlertCircle } from 'lucide-react';

const Freelancers: React.FC = () => {
  const [freelancers, setFreelancers] = useState<Freelancer[]>([]);
  const [filteredFreelancers, setFilteredFreelancers] = useState<Freelancer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [skillFilter, setSkillFilter] = useState('');
  const [rateFilter, setRateFilter] = useState('');

  useEffect(() => {
    loadFreelancers();
  }, []);

  useEffect(() => {
    filterFreelancers();
  }, [freelancers, searchTerm, skillFilter, rateFilter]);

  const loadFreelancers = async () => {
    try {
      const data = await freelancerService.getAll();
      setFreelancers(data);
    } catch (err) {
      setError('Failed to load freelancers');
      console.error('Error loading freelancers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterFreelancers = () => {
    let filtered = [...freelancers];

    // Search by name, email, or bio
    if (searchTerm) {
      filtered = filtered.filter(freelancer =>
        freelancer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        freelancer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (freelancer.bio && freelancer.bio.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by skills
    if (skillFilter) {
      filtered = filtered.filter(freelancer =>
        freelancer.skills.some(skill =>
          skill.toLowerCase().includes(skillFilter.toLowerCase())
        )
      );
    }

    // Filter by cost per project
    if (rateFilter) {
      const maxRate = parseFloat(rateFilter);
      if (!isNaN(maxRate)) {
        filtered = filtered.filter(freelancer => freelancer.cost_per_project <= maxRate);
      }
    }

    setFilteredFreelancers(filtered);
  };

  const FreelancerCard: React.FC<{ freelancer: Freelancer }> = ({ freelancer }) => (
    <Link to={`/freelancer/${freelancer.id}`} className="block">
      <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-4 sm:p-6 hover:shadow-xl transition-all duration-300 cursor-pointer hover:bg-gray-800/60">
      <div className="flex items-start justify-between mb-3 sm:mb-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-white truncate">{freelancer.name}</h3>
          <p className="text-xs sm:text-sm text-white/70 truncate">{freelancer.email}</p>
        </div>
        <div className="flex items-center space-x-2 ml-2">
          <span className="text-xs sm:text-sm font-medium text-primary-400">
            ${freelancer.cost_per_project}
          </span>
          <div className={`w-2 h-2 rounded-full ${freelancer.availability ? 'bg-green-400' : 'bg-red-400'}`} />
        </div>
      </div>

      <div className="mb-3 sm:mb-4">
        <div className="flex items-center space-x-2 mb-2">
          <Clock size={14} className="text-white/50 flex-shrink-0" />
          <span className="text-xs sm:text-sm text-white/70">
            {freelancer.experience_years} years experience
          </span>
        </div>
      </div>

      {freelancer.bio && (
        <p className="text-xs sm:text-sm text-white/80 mb-3 sm:mb-4 line-clamp-2">{freelancer.bio}</p>
      )}

      <div className="mb-3 sm:mb-4">
        <h4 className="text-xs sm:text-sm font-medium text-white mb-2">Skills:</h4>
        <div className="flex flex-wrap gap-1 sm:gap-2">
          {freelancer.skills.slice(0, 5).map((skill, index) => (
            <span
              key={index}
              className="px-2 py-1 text-xs rounded-full bg-primary-500/20 text-primary-300 border border-primary-500/30"
            >
              {skill}
            </span>
          ))}
          {freelancer.skills.length > 5 && (
            <span className="px-2 py-1 text-xs rounded-full bg-primary-500/20 text-primary-300 border border-primary-500/30">
              +{freelancer.skills.length - 5} more
            </span>
          )}
        </div>
      </div>

      {freelancer.portfolio_url && (
        <a
          href={freelancer.portfolio_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-400 hover:text-primary-300 text-xs sm:text-sm font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          View Portfolio →
        </a>
      )}
      </div>
    </Link>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-white/70">Loading freelancers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={40} className="mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Error</h2>
          <p className="text-white/70 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Browse Freelancers
          </h1>
          <p className="text-base sm:text-lg text-white/70">
            Discover talented professionals ready to work on your projects
          </p>
        </div>

        {/* Filters */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name, email, or bio..."
                    className="w-full pl-10 pr-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white placeholder-white/50"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search size={16} className="absolute left-3 top-2.5 text-white/40" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Skills
                </label>
                <input
                  type="text"
                  placeholder="Filter by skills..."
                  className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white placeholder-white/50"
                  value={skillFilter}
                  onChange={(e) => setSkillFilter(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Max Cost per Project ($)
                </label>
                <input
                  type="number"
                  placeholder="e.g., 100"
                  className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base bg-white/5 text-white placeholder-white/50"
                  value={rateFilter}
                  onChange={(e) => setRateFilter(e.target.value)}
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSkillFilter('');
                    setRateFilter('');
                  }}
                  className="w-full bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-md transition-colors text-sm sm:text-base border border-white/20"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <h2 className="text-lg sm:text-xl font-semibold text-white">
                {filteredFreelancers.length} freelancer{filteredFreelancers.length !== 1 ? 's' : ''} found
              </h2>
              <div className="flex items-center space-x-2 text-xs sm:text-sm text-white/60">
                <Filter size={14} className="sm:w-4 sm:h-4" />
                <span>Filtered results</span>
              </div>
            </div>
          </div>

          {/* Freelancers Grid */}
          {filteredFreelancers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filteredFreelancers.map((freelancer, index) => (
                <FreelancerCard key={freelancer.id || index} freelancer={freelancer} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 sm:py-12">
              <Users size={40} className="mx-auto text-white/40 mb-4 sm:w-12 sm:h-12" />
              <h3 className="text-base sm:text-lg font-medium text-white mb-2">No freelancers found</h3>
              <p className="text-sm sm:text-base text-white/70">
                {searchTerm || skillFilter || rateFilter 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No freelancers are currently available.'
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Freelancers; 